<template>
  <div>
    <MrpaUploader
      :v="$v"
      @parsed-mrpa-change="parsedMrpaChangeHandler"
      :statement-id="statementId"
      :statement-type="originalStatementType"
    />

    <MrpaTrustee
      v-if="formVisible"
      :role="role"
      :original-statement-id="originalStatementId"
      :label="getTrusteeLabel(role)"
      :v="$v.trusteeInfo"
    />

    <slot name="footer" :next="nextHandler"></slot>
  </div>
</template>

<script>
import cloneDeep from 'clone-deep';

import MrpaUploader, {
  mrpaUploaderModel,
  mrpaUploaderValidation,
} from '@/components/services/elements/MrpaUploader';
import { getValidationErrorText } from '@/utils/validate';
import { Mask, MrpaType, StatementType } from '@/constants';
import deepmerge from 'deepmerge';
import { MrpaSubjectModule } from '@/store/modules/mrpaSubject';
import { getTrusteeLabel } from '@/utils/common';
import MrpaTrustee, {
  mrpaTrusteeModel,
  getMrpaTrusteeValidation,
} from '@/components/services/elements/MrpaTrustee';
import { assistantModel } from '@/components/services/elements/Assistant';

export default {
  name: 'AddStMrpaTrustee',
  props: {
    originalStatementId: {
      type: String,
      required: true,
    },
    originalStatementType: {
      type: String,
      required: true,
    },
    trusteeInfoProp: {
      type: Object,
    },
    role: {
      type: String,
      required: true,
    },
  },
  components: {
    MrpaUploader,
    MrpaTrustee,
  },
  data: () => ({
    Mask,
    getTrusteeLabel,
    getValidationErrorText,
    isCheckMrpaError: false,
    mrpa: cloneDeep(mrpaUploaderModel.mrpa),
    formVisible: mrpaUploaderModel.formVisible,
    trusteeInfo: mrpaTrusteeModel,
  }),
  computed: {
    statementId() {
      return this.originalStatementType === StatementType.ACCEPTANCE_ESCROW
        ? null
        : this.originalStatementId;
    },
  },
  validations() {
    return {
      ...mrpaUploaderValidation,
      trusteeInfo: getMrpaTrusteeValidation(this.trusteeInfo),
    };
  },
  created() {
    if (this.trusteeInfoProp) this.prefillDraft();
  },
  methods: {
    async prefillDraft() {
      const assistantInfo = this.trusteeInfoProp.assistantInfo
        ? deepmerge(assistantModel, this.trusteeInfoProp.assistantInfo)
        : {};

      this.trusteeInfo = deepmerge.all([
        this.trusteeInfo,
        this.trusteeInfoProp,
        {
          ...this.trusteeInfoProp.notaryInfo,
          ...assistantInfo,
        },
      ]);

      const { issueNumber, mrpIssuerInn } = this.trusteeInfo.baseAuthority;

      if (this.trusteeInfo.documents?.authorityDocument?.file) {
        const { file, fileSig } = this.trusteeInfo.documents.authorityDocument;
        this.mrpa.file = file;
        this.mrpa.fileSig = fileSig;
        this.mrpa.type = MrpaType.BY_FILE;
      } else {
        this.mrpa.guid = issueNumber;
        this.mrpa.issuerInn = mrpIssuerInn;
        this.mrpa.type = MrpaType.BY_GUID;
      }

      this.formVisible = true;

      await this.checkMrpa();
    },

    async scrollToTop() {
      await this.$vuetify.goTo('#header-target', { duration: 200 });
    },

    async checkMrpa() {
      try {
        if (this.mrpa.type === MrpaType.BY_FILE) {
          await MrpaSubjectModule.checkMrpaSubjectFile({
            sig: this.mrpa.fileSig?.link,
            xml: this.mrpa.file?.link,
            statementId: this.statementId,
            statementType: this.originalStatementType,
          });
        }

        if (this.mrpa.type === MrpaType.BY_GUID) {
          await MrpaSubjectModule.checkMrpaSubjectGuid({
            guid: this.mrpa.guid,
            issuerInn: this.mrpa.issuerInn,
            statementId: this.statementId,
            statementType: this.originalStatementType,
          });
        }
      } catch (e) {
        this.isCheckMrpaError = true;
        await this.scrollToTop();
      }
    },

    parsedMrpaChangeHandler(parsedMrpa) {
      if (parsedMrpa) {
        this.fillMrpa(parsedMrpa);
        this.formVisible = true;
      } else {
        this.isCheckMrpaError = false;
        this.formVisible = null;
      }
    },

    fillMrpa(parsedMrpa) {
      this.trusteeInfo = deepmerge(mrpaTrusteeModel, parsedMrpa.trustee);
    },

    assemblyModel() {
      return {
        lastName: this.trusteeInfo.lastName,
        firstName: this.trusteeInfo.firstName || this.trusteeInfo.name,
        patronymic: this.trusteeInfo.patronymic,
        trusteeStatus: this.trusteeInfo.trusteeStatus,
        snils: this.trusteeInfo.snils,
        inn: this.trusteeInfo.inn,
        documents: {
          statusDocument: this.trusteeInfo.documents.statusDocument.name
            ? this.trusteeInfo.documents.statusDocument
            : null,
          authorityDocument:
            this.mrpa.type === MrpaType.BY_GUID
              ? null
              : this.trusteeInfo.documents.authorityDocument,
        },
        baseAuthority: {
          ...this.trusteeInfo.baseAuthority,
          mrpIssuerInn:
            this.mrpa.type === MrpaType.BY_GUID ? this.mrpa.issuerInn : null,
        },
        notaryInfo: {
          notaryJob: this.trusteeInfo.notaryJob,
          notaryRegNumber: this.trusteeInfo.notaryRegNumber,
          notaryFirstName: this.trusteeInfo.notaryFirstName,
          notaryLastName: this.trusteeInfo.notaryLastName,
          notaryPatronymic: this.trusteeInfo.notaryPatronymic,
        },
        assistantInfo: this.trusteeInfo.assistantJob
          ? {
              assistantFirstName: this.trusteeInfo.assistantFirstName,
              assistantJob: this.trusteeInfo.assistantJob,
              assistantLastName: this.trusteeInfo.assistantLastName,
              assistantPatronymic: this.trusteeInfo.assistantPatronymic,
              assistantRegNumber: this.trusteeInfo.assistantRegNumber,
            }
          : {},

        email: this.trusteeInfo.email,
        phone: this.trusteeInfo.phone,
      };
    },

    nextHandler() {
      this.$v.formVisible.$touch();

      if (this.$v.formVisible.$invalid) {
        this.scrollToTop();
        return;
      }

      this.$v.trusteeInfo.$touch();

      if (this.$v.trusteeInfo.$invalid) {
        this.scrollToTop();
        return;
      }

      this.$emit('send', this.assemblyModel());
    },
  },
};
</script>
